// apps/client/src/characters/walker.ts — your resident's movement: port of look-demo avatar.ts `Walker` (§8.3 movement system).
// Steering with a smooth turn, circle push-out against other residents, ground following, sprint/walk/idle choice. The space
// decides every step (rev 7 item 18): `canMove(from, to)` on the {x, y} tile plane (y = world z), else `walkable(to)`.
import { tuning } from '@whiskerton/content';
import type { CatalogId, MoveAnim, MoveState, Vec2 } from '@whiskerton/shared';
import * as THREE from 'three';
import type { SpaceScene } from '../game/spaces';
import type { Avatar } from './avatar';

const TURN = 10, ACCEL = 10, GROUND = 14;    // look-demo response rates (per second): feel of the port, not gameplay numbers
const MIN_INPUT = 0.05, MIN_SPEED = 0.02, WALK_ANIM = 0.3, RUN_ANIM = 1.25;   // animation thresholds (look demo): run above walk × 1.25
const TAU = Math.PI * 2;
/** Wraps an angle into (-π, π], so facing never grows as the player circles (the server bounds `f`). */
const wrapAngle = (a: number) => a - TAU * Math.floor((a + Math.PI) / TAU);
const lerpAngle = (a: number, b: number, t: number) => { const d = wrapAngle(b - a); return wrapAngle(a + d * Math.min(1, t)); };

export class Walker {
  pos = new THREE.Vector3();
  yaw = 0;
  speed = 0;
  private readonly avatar: Avatar;
  private readonly space: Pick<SpaceScene, 'heightAt' | 'walkable' | 'canMove'>;
  private readonly others: () => { x: number; z: number; r: number }[];
  /** Direction of travel on the ground plane (x, z); it follows the stick at once while the body turns smoothly towards it. */
  private dir = { x: 0, z: 1 };
  constructor(avatar: Avatar, space: Pick<SpaceScene, 'heightAt' | 'walkable' | 'canMove'>, others: () => { x: number; z: number; r: number }[]) {
    this.avatar = avatar; this.space = space; this.others = others;
  }
  /** Puts the walker down at (x, z) (tile plane x, y), facing `yaw`, standing still. */
  place(x: number, z: number, yaw = 0): void {
    this.pos.set(x, this.space.heightAt({ x, y: z }), z);
    this.yaw = wrapAngle(yaw);
    this.dir = { x: Math.sin(yaw), z: Math.cos(yaw) };
    this.speed = 0;
    this.avatar.setMove('idle', 0);
    this.sync();
  }
  /** One fixed step: (dx, dz) is the wanted direction on the ground plane (length ≤ 1 = stick deflection). */
  move(dt: number, dx: number, dz: number, running: boolean): void {
    const M = tuning.move, mag = Math.hypot(dx, dz);
    const want = mag > MIN_INPUT ? (running ? M.run : M.walk) * Math.min(1, mag) : 0;
    this.speed += (want - this.speed) * Math.min(1, dt * ACCEL);
    if (want === 0 && this.speed <= WALK_ANIM) this.speed = 0;   // stop as the doll goes idle: no slide, so one idle `mv` frame
    if (mag > MIN_INPUT) {
      this.dir = { x: dx / mag, z: dz / mag };
      this.yaw = lerpAngle(this.yaw, Math.atan2(dx, dz), dt * TURN);
    }
    if (this.speed > MIN_SPEED) {
      const step = this.speed * dt, from: Vec2 = { x: this.pos.x, y: this.pos.z }, blockers = this.others();
      const tryMove = (nx: number, nz: number): boolean => {
        for (const c of blockers) {                                // circle push-out against other residents (look demo)
          const ox = nx - c.x, oz = nz - c.z, d = Math.hypot(ox, oz), min = c.r + M.radius;
          if (d < min && d > 1e-4) { nx = c.x + (ox / d) * min; nz = c.z + (oz / d) * min; }
        }
        if (!this.allowed(from, { x: nx, y: nz })) return false;
        this.pos.x = nx; this.pos.z = nz;
        return true;
      };
      const sx = this.dir.x * step, sz = this.dir.z * step;
      // Full step, then x only, then y only: sliding along whatever refused the diagonal (the demo's order).
      if (!tryMove(this.pos.x + sx, this.pos.z + sz) && !(Math.abs(sx) > 1e-6 && tryMove(this.pos.x + sx, this.pos.z)) && Math.abs(sz) > 1e-6) tryMove(this.pos.x, this.pos.z + sz);
    }
    this.pos.y += (this.space.heightAt({ x: this.pos.x, y: this.pos.z }) - this.pos.y) * Math.min(1, dt * GROUND);
    this.avatar.setMove(this.anim(), this.speed);
    this.sync();
  }
  /** What the server relays to everyone else (§4.2 `mv`). */
  state(tool: CatalogId | null): MoveState {
    return { x: this.pos.x, y: this.pos.z, f: this.yaw, a: this.anim(), tool };
  }
  private anim(): MoveAnim {
    return this.speed > tuning.move.walk * RUN_ANIM ? 'run' : this.speed > WALK_ANIM ? 'walk' : 'idle';
  }
  private allowed(from: Vec2, to: Vec2): boolean {
    return this.space.canMove ? this.space.canMove(from, to) : this.space.walkable(to);
  }
  private sync(): void {
    this.avatar.root.position.copy(this.pos);
    this.avatar.root.rotation.y = this.yaw;
  }
}
